import * as React from 'react';
import * as _ from 'underscore';
import * as $ from 'jquery';

import { IGlob } from '../../../../pdc-platform/src/ts/Glob';
import TreeItemGeneral, { IToggleStates } from '../TreeItemGeneral';

import {
    ATTRIBUTE_SEPARATOR,
    IDataLoadEntity,
    ITreeItem,
    DataloadEventTypes
} from '../../../../pdc-platform/src/ts/stores/EntityModelsStore';

export interface IAttributeSelectOptions {
    tree: ITreeItem[];
    collapsedDict?: IToggleStates;
    multiselect?: boolean;
    checkedItems?: IToggleStates;
    validEntities?: string[];
    doNotAddSpecialEntities?: boolean;

    onCollapsedDictChange?(value: IToggleStates): void;
}

function getDefaultFocusedTreeItemID(value: string, tree: ITreeItem[]) {
    if (value != null) {
        return value;
    } else if (tree != null && tree.length > 0) {
        return tree[0].id;
    }

    return undefined;
}

function createValidEntitiesTree(
    value: string,
    dataSourceID: number,
    tree: ITreeItem[],
    options: IAttributeSelectOptions,
    glob: IGlob
): ITreeItem[] {
    if (options.validEntities == null) {
        return null;
    }

    let validEntities: string[];

    if (options.validEntities.length === 0) {
        if (value != null) {
            validEntities = [value.split(ATTRIBUTE_SEPARATOR)[0]];
        } else {
            return null;
        }
    } else {
        validEntities = [...options.validEntities];
    }

    const result: ITreeItem[] = [];

    validEntities.forEach((id) => {
        for (let i = 0; i < tree.length; i++) {
            const item = tree[i];

            if (item.id === id && item.original?.IsScalarFunc !== true) {
                item.backgroundColor = 'aliceblue';
                result.push({ ...item });
                break;
            }
        }
    });

    if (options.doNotAddSpecialEntities !== true) {
        const entities = glob.EntityModelsStore.getEntitiesHashByDS(dataSourceID);

        if (entities != null) {
            // Add special Entities as #EnumerateDates.
            for (let attribute in entities) {
                if (attribute[0] === '#') {
                    const firstEntity = attribute.split(ATTRIBUTE_SEPARATOR)[0];

                    if (validEntities.indexOf(firstEntity) < 0) {
                        validEntities.push(firstEntity);
                    }
                }
            }
        }
    }

    return result;
}

function isExpanded(item: ITreeItem, collapsedDict: IToggleStates) {
    return item != null && item.expandable && collapsedDict != null && collapsedDict[item.id] === false;
}

function itemHasExpandedChildren(item: ITreeItem, collapsedDict: IToggleStates) {
    return isExpanded(item, collapsedDict) && item.children != null && item.children.length > 0;
}

function expandTheOnlyItems(tree: ITreeItem[], collapsedDict: IToggleStates) {
    if (tree != null && tree.length === 1) {
        const item = tree[0];

        if (item.expandable) {
            collapsedDict[item.id] = false;
            expandTheOnlyItems(item.children, collapsedDict);
        }
    }
}

function getLastChild(tree: ITreeItem[], collapsedDict: IToggleStates): ITreeItem {
    if (tree != null && tree.length > 0) {
        let item = tree[tree.length - 1];

        if (itemHasExpandedChildren(item, collapsedDict)) {
            item = getLastChild(item.children, collapsedDict);
        }

        return item;
    }

    return;
}

function findTreeItem(id: string, tree: ITreeItem[]) {
    if (tree == null || tree.length === 0) {
        return;
    }

    for (let i = 0; i < tree.length; i++) {
        let item = tree[i];

        if (item.id === id) {
            return item;
        } else if (item.expandable) {
            item = findTreeItem(id, item.children);

            if (item != null) {
                return item;
            }
        }
    }

    return;
}

function getVisibleItems(result: ITreeItem[], tree: ITreeItem[], collapsedDict: IToggleStates) {
    if (tree == null) {
        return;
    }

    tree.forEach((item) => {
        result.push(item);

        if (collapsedDict != null) {
            const isExpanded = _.find(_.keys(collapsedDict), (key) => {
                return key === item.id;
            });

            if (isExpanded) {
                getVisibleItems(result, item.children, collapsedDict);
            }
        }
    });
}

function fixAttributeNames(
    names: string[],
    fullCollection: IDataLoadEntity[],
    attributes: IDataLoadEntity[],
    index: number,
    result: string[]
) {
    if (index >= names.length) {
        return;
    }

    let name = names[index];

    if (name.length === 0) {
        result.push('');
        return;
    }

    name = name.toLowerCase();

    const entity = _.find(attributes, (entity) => {
        return entity.name.toLowerCase() === name;
    });

    if (entity != null) {
        result.push(entity.name);

        if (entity.attributes != null) {
            fixAttributeNames(names, fullCollection, entity.attributes, index + 1, result);
        } else {
            const refer = entity.refer;

            if (refer != null) {
                const referer = _.find(fullCollection, (entity) => {
                    return entity.name === refer;
                });

                if (referer.attributes != null) {
                    fixAttributeNames(names, fullCollection, referer.attributes, index + 1, result);
                }
            }
        }
    } else {
        for (let i = index; i < names.length; i++) {
            result.push(names[i]);
        }
    }
}

function fillChildItems(source: ITreeItem[], target: ITreeItem[]) {
    if (source == null || target == null) {
        return;
    }

    target.forEach((item) => {
        if (item.expandable) {
            const id = item.id;
            const sourceItem = source.find((item) => {
                return item.id === id;
            });

            if (sourceItem != null && sourceItem.children.length > 0) {
                if (item.children.length === 0) {
                    sourceItem.children.forEach((child) => {
                        item.children.push(child);
                    });
                } else {
                    fillChildItems(sourceItem.children, item.children);
                }
            }
        }
    });
}

function findNavigationTreeItems(
    id: string,
    tree: ITreeItem[],
    collapsedDict: IToggleStates,
    parentItems?: {
        previousSibling: ITreeItem;
        nextSibling: ITreeItem;
    }
): {
    itemUp: ITreeItem;
    itemDown: ITreeItem;
} {
    if (tree != null) {
        const treeLength = tree.length;

        for (let i = 0; i < treeLength; i++) {
            const item = tree[i];
            const hasExpandedChildren = itemHasExpandedChildren(item, collapsedDict);

            if (item.id === id) {
                let nextItem: ITreeItem;

                if (hasExpandedChildren) {
                    nextItem = item.children[0];
                } else if (i + 1 < treeLength) {
                    nextItem = tree[i + 1];
                } else if (parentItems != null) {
                    nextItem = parentItems.nextSibling;
                }

                let previousItem: ITreeItem;

                if (i > 0) {
                    previousItem = tree[i - 1];

                    if (isExpanded(previousItem, collapsedDict)) {
                        const previousItemLastChild = getLastChild(previousItem.children, collapsedDict);

                        if (previousItemLastChild != null) {
                            previousItem = previousItemLastChild;
                        }
                    }
                }

                if (previousItem == null && parentItems != null) {
                    previousItem = parentItems.previousSibling;
                }

                return {
                    itemUp: previousItem,
                    itemDown: nextItem
                };
            } else if (hasExpandedChildren) {
                const result = findNavigationTreeItems(id, item.children, collapsedDict, {
                    previousSibling: item,
                    nextSibling: i + 1 < treeLength ? tree[i + 1] : undefined
                });

                if (result != null) {
                    return result;
                }
            }
        }
    }

    return;
}

function addItemToTree(
    item: ITreeItem,
    attributes: string[],
    attributeIndex: number,
    result: ITreeItem[],
    foundAttribute?: string[]
): boolean {
    const itemCopy: ITreeItem = {
        id: item.id,
        name: item.name,
        expandable: item.expandable,
        children: item.children != null ? [] : undefined,
        isKeyAttribute: item.isKeyAttribute,
        isDefaultAttribute: item.isDefaultAttribute
    };

    result.push(itemCopy);

    return reduceTreeToAttribute(item.children, attributes, attributeIndex + 1, itemCopy.children, foundAttribute);
}

function reduceTreeToAttribute(
    originalTree: ITreeItem[],
    attributesToSearch: string[],
    currentAttributeIndex: number,
    result: ITreeItem[],
    foundAttribute: string[]
): boolean {
    if (originalTree == null) {
        return false;
    }

    let attributeIndexIsValid = currentAttributeIndex < attributesToSearch.length;
    let searchingIsValid = false;

    if (attributeIndexIsValid) {
        let attribute = attributesToSearch[currentAttributeIndex].trim();

        if (attribute.length > 0) {
            if (currentAttributeIndex < attributesToSearch.length - 1) {
                // Name of previous attibutes is corrected already. So there can be a single attribute only.
                for (let i = 0; i < originalTree.length; i++) {
                    const item = originalTree[i];

                    if (item.name === attribute) {
                        if (foundAttribute != null) {
                            foundAttribute.push(item.name);
                        }

                        searchingIsValid = addItemToTree(
                            item,
                            attributesToSearch,
                            currentAttributeIndex,
                            result,
                            foundAttribute
                        );
                        break;
                    }
                }
            } else {
                // Add all the items that begin as the last attribute.
                // And add all Reverse Attributes starting with the same text.
                const loweredAttribute = attribute.toLowerCase();

                // If it is not already Reverse Attribute, cunstruct one to search for.
                const loweredReverseAttribute = loweredAttribute[0] !== '^' ? '^' + loweredAttribute : null;

                originalTree.forEach((item) => {
                    const loweredName = item.name.toLowerCase();

                    if (
                        loweredName.indexOf(loweredAttribute) === 0 ||
                        (loweredReverseAttribute != null && loweredName.indexOf(loweredReverseAttribute) === 0)
                    ) {
                        if (foundAttribute != null && item.name === attribute) {
                            foundAttribute.push(item.name);
                        }

                        searchingIsValid = addItemToTree(
                            item,
                            attributesToSearch,
                            currentAttributeIndex,
                            result,
                            foundAttribute
                        );
                    }
                });
            }
        } else {
            attributeIndexIsValid = false;
        }
    }

    if (!attributeIndexIsValid) {
        // Add all the rest items.
        originalTree.forEach((item) => {
            addItemToTree(item, attributesToSearch, currentAttributeIndex, result);
        });

        searchingIsValid = true;
    }

    return searchingIsValid;
}

// Change scrolliing to make selected item visible.
function makeSelectedItemVisible(
    value: string,
    tree: ITreeItem[],
    collapsedDict: IToggleStates,
    treeContainer: HTMLDivElement
) {
    if (value != null && value.length > 0) {
        const visibleItems = [];

        getVisibleItems(visibleItems, tree, collapsedDict);

        const selectedItem = value;

        for (let itemIndex = 0; itemIndex < visibleItems.length; itemIndex++) {
            const item = visibleItems[itemIndex];

            if (item.id === selectedItem) {
                let pos = itemIndex;

                if (itemIndex > 4) {
                    pos = itemIndex - 5;
                }

                treeContainer.scrollTop = (pos / visibleItems.length) * treeContainer.scrollHeight;
            }
        }
    }
}

export interface IAttributeSelectProperties {
    dataSourceID: number;
    value: string;
    checkedItems: IToggleStates;
    options: IAttributeSelectOptions;
    glob: IGlob;

    onChange(value: string): void;
    onCheckedChange(id: string, value: boolean): void;
    onApply(selectedItem: string): void;
    onClose(): void;
}

interface IAttributeSelectState {
    value?: string;
    editorValue?: string;
    collapsedDict?: IToggleStates;
    tree?: ITreeItem[];
    focusedItemID?: string;
    firstValidItemID?: string;
}

function createNewState(
    searchFieldValue: string,
    originalTree: ITreeItem[],
    originalValue: string,
    collapsedDict: IToggleStates,
    dataSourceID: number,
    glob: IGlob
) {
    let result: IAttributeSelectState;

    if (searchFieldValue.length > 0) {
        const names = searchFieldValue.split(ATTRIBUTE_SEPARATOR);
        const entities = glob.EntityModelsStore.getEntitiesByDS(dataSourceID);
        const attributesToSearch: string[] = [];
        const newCollapsedDict = { ...collapsedDict };

        let foundAttribute: string[] = [];
        let reducedTree: ITreeItem[] = [];

        fixAttributeNames(names, entities, entities, 0, attributesToSearch);

        if (reduceTreeToAttribute(originalTree, attributesToSearch, 0, reducedTree, foundAttribute)) {
            expandTheOnlyItems(reducedTree, newCollapsedDict);
        } else {
            foundAttribute = [];
            reducedTree = [];
        }

        const newValue = foundAttribute.length > 0 ? foundAttribute.join(ATTRIBUTE_SEPARATOR) : null;

        let newFocusedItemID: string;

        if (newValue != null) {
            const valueItem = findTreeItem(newValue, reducedTree);

            if (itemHasExpandedChildren(valueItem, newCollapsedDict)) {
                newFocusedItemID = valueItem.children[0].id;
            }
        }

        if (newFocusedItemID == null) {
            newFocusedItemID = getDefaultFocusedTreeItemID(newValue, reducedTree);
        }

        result = {
            value: newValue,
            editorValue: attributesToSearch.join(ATTRIBUTE_SEPARATOR),
            collapsedDict: newCollapsedDict,
            tree: reducedTree,
            focusedItemID: newFocusedItemID
        };
    } else {
        result = {
            value: originalValue,
            editorValue: '',
            collapsedDict: collapsedDict,
            tree: [...originalTree],
            focusedItemID: getDefaultFocusedTreeItemID(originalValue, originalTree)
        };
    }

    return result;
}

function createInitialState(
    value: string,
    options: IAttributeSelectOptions,
    dataSourceID: number,
    glob: IGlob
): IAttributeSelectState {
    const collapsedDict = options.collapsedDict ?? {};
    const tree = [...options.tree];
    const validEntitiesTree = createValidEntitiesTree(value, dataSourceID, tree, options, glob);

    if (value != null) {
        const dicElements = value.split(ATTRIBUTE_SEPARATOR);

        let selectedElement = '';

        for (let elemIndex = 0; elemIndex < dicElements.length - 1; elemIndex++) {
            if (selectedElement.length > 0) {
                selectedElement += ATTRIBUTE_SEPARATOR;
            }

            selectedElement += dicElements[elemIndex];
            collapsedDict[selectedElement] = false;
        }
    }

    let firstValidItemID: string;

    if (validEntitiesTree != null && validEntitiesTree.length > 0) {
        firstValidItemID = validEntitiesTree[0].id;
        validEntitiesTree.forEach((item) => {
            collapsedDict[item.id] = false;
        });
    }

    if (tree != null && tree.length === 1) {
        // Expand the only item.
        collapsedDict[tree[0].id] = false;
    }

    return {
        value: value,
        editorValue: value || '',
        collapsedDict: collapsedDict,
        tree: tree,
        focusedItemID: getDefaultFocusedTreeItemID(value ?? firstValidItemID, tree),
        firstValidItemID: firstValidItemID
    };
}

export default function AttributeSelect(props: IAttributeSelectProperties) {
    const [state, setState] = React.useState(
        createInitialState(props.value, props.options, props.dataSourceID, props.glob)
    );

    const searchFieldIsFocused = React.useRef(true);

    const containerRef = React.useRef<HTMLDivElement>(null);
    const searchFieldRef = React.useRef<HTMLInputElement>(null);
    const treeContainerRef = React.useRef<HTMLDivElement>(null);
    const treeControlRef = React.useRef<HTMLUListElement>(null);

    React.useEffect(() => {
        makeSelectedItemVisible(
            props.value ?? state.firstValidItemID,
            state.tree,
            state.collapsedDict,
            treeContainerRef.current
        );

        const modalBody = $(containerRef.current).closest('.modal-body');
        const height = $(modalBody).height();

        // Embed tree into the attribute-select container
        const handleOnResize = (event, dialogHeight) => {
            const height = dialogHeight - $(containerRef.current).offset().top;

            $(treeContainerRef.current).css('height', height + 'px');
        };

        $(containerRef.current).on('container-resize', handleOnResize);
        handleOnResize(null, height);

        return () => {
            $(containerRef.current).off('container-resize', handleOnResize);
        };
    }, []);

    React.useEffect(() => {
        const handleOnEntityModelsStoreChange = (eventType: DataloadEventTypes) => {
            if (eventType === DataloadEventTypes.MODEL_LEAF) {
                const source = props.glob.EntityModelsStore.getEntitiesTreeByDS(props.dataSourceID);
                const target = [...state.tree]; // Making copy by extend may exceed max depth level.

                fillChildItems(source, target);
                setState({
                    ...state,
                    tree: target
                });
            }
        };

        props.glob.EntityModelsStore.addOnDataLoadListener(handleOnEntityModelsStoreChange);

        return () => {
            props.glob.EntityModelsStore.removeOnDataLoadListener(handleOnEntityModelsStoreChange);
        };
    }, [state]);

    React.useEffect(() => {
        if (searchFieldIsFocused.current) {
            searchFieldRef.current?.focus();
        } else {
            treeControlRef.current?.focus();
        }
    });

    const handleOnSearchFieldFocus = React.useCallback(() => {
        searchFieldIsFocused.current = true;
    }, []);

    const handleOnSearchFieldBlur = React.useCallback(() => {
        searchFieldIsFocused.current = false;
    }, []);

    const handleOnLoadLeafCall = React.useCallback(
        (attributeName: string) => {
            props.glob.EntityModelsActions.loadModelLeafOnDemand(attributeName, props.dataSourceID);
        },
        [props.dataSourceID]
    );

    const handleOnCollapsedTreeItemChange = React.useCallback(
        (id: string, collapsed: boolean) => {
            const newCollapsedDict = { ...state.collapsedDict };

            newCollapsedDict[id] = collapsed;
            setState({ ...state, collapsedDict: newCollapsedDict });
        },
        [state]
    );

    const moveFocus = (direction: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT') => {
        let focusedItemID = state.focusedItemID;

        if (focusedItemID == null) {
            return;
        }

        const currentItems = findNavigationTreeItems(focusedItemID, state.tree, state.collapsedDict);

        if (currentItems == null) {
            return;
        }

        let valueItem: ITreeItem;

        switch (direction) {
            case 'DOWN':
                if (currentItems.itemDown != null) {
                    focusedItemID = currentItems.itemDown.id;
                }
                break;

            case 'UP':
                if (currentItems.itemUp != null) {
                    focusedItemID = currentItems.itemUp.id;
                } else {
                    searchFieldRef.current?.focus();
                    return;
                }
                break;

            case 'LEFT':
                valueItem = findTreeItem(focusedItemID, state.tree);

                if (valueItem.expandable) {
                    handleOnCollapsedTreeItemChange(focusedItemID, true);
                }

                return;

            case 'RIGHT':
                valueItem = findTreeItem(focusedItemID, state.tree);

                if (valueItem.expandable) {
                    if (valueItem.children?.length > 0) {
                        handleOnCollapsedTreeItemChange(focusedItemID, false);
                    } else {
                        state.collapsedDict[focusedItemID] = false;
                        handleOnLoadLeafCall(focusedItemID);
                    }
                }

                return;
        }

        if (focusedItemID !== state.focusedItemID) {
            searchFieldIsFocused.current = false;
            setState({ ...state, focusedItemID: focusedItemID });
        }
    };

    const createAndApplyNewState = (searchFieldValue: string) => {
        const newState = createNewState(
            searchFieldValue,
            props.options.tree,
            props.value,
            state.collapsedDict,
            props.dataSourceID,
            props.glob
        );

        setState(newState);
        props.onChange(newState.value);
    };

    const applyFocusedItemToEditor = () => {
        if (state.focusedItemID != null) {
            createAndApplyNewState(state.focusedItemID);
        }
    };

    const handleOnEditorChange = (e) => {
        const searchFieldValue = searchFieldRef.current.value.trimLeft();

        createAndApplyNewState(searchFieldValue);
    };

    const processOnCollapsedDictChange = React.useCallback(() => {
        if (props.options.onCollapsedDictChange != null) {
            props.options.onCollapsedDictChange(state.collapsedDict);
        }
    }, [state.collapsedDict, props.options.onCollapsedDictChange]);

    const processOnApply = React.useCallback(
        (selectedItemID: string) => {
            processOnCollapsedDictChange();
            props.onApply(selectedItemID);
        },
        [processOnCollapsedDictChange, props.onApply]
    );

    const handleOnDoubleClick = () => {
        processOnApply(state.value);
    };

    const handleOnKeyDown = (e) => {
        switch (e.code) {
            case 'Tab':
                applyFocusedItemToEditor();
                e.stopPropagation();
                e.preventDefault();
                break;

            case 'Escape':
                processOnCollapsedDictChange();
                props.onClose();
                break;

            case 'Enter':
                if (state.focusedItemID != null) {
                    const newState = createNewState(
                        state.focusedItemID,
                        props.options.tree,
                        props.value,
                        state.collapsedDict,
                        props.dataSourceID,
                        props.glob
                    );

                    processOnApply(newState.value);
                }
                break;

            case 'Period':
                applyFocusedItemToEditor();
                break;

            case 'Space':
                processSpacePress(e);
                break;

            case 'ArrowUp':
                e.preventDefault();
                moveFocus('UP');
                break;

            case 'ArrowDown':
                e.preventDefault();
                moveFocus('DOWN');
                break;

            case 'ArrowLeft':
                if (!searchFieldIsFocused.current) {
                    e.preventDefault();
                    moveFocus('LEFT');
                }
                break;

            case 'ArrowRight':
                if (!searchFieldIsFocused.current) {
                    e.preventDefault();
                    moveFocus('RIGHT');
                }
                break;

            default:
                if (!searchFieldIsFocused.current) {
                    searchFieldRef.current?.focus();
                }
        }
    };

    const processSpacePress = (e) => {
        const { focusedItemID } = state;

        e.stopPropagation();
        e.preventDefault();

        if (searchFieldIsFocused.current || !props.options.multiselect || focusedItemID == null) {
            return;
        }

        props.onCheckedChange(focusedItemID, props.checkedItems?.[focusedItemID] !== true);
    };

    const handleOnItemSelect = (value: string) => {
        const newState: IAttributeSelectState = {
            ...state,
            value: value,
            focusedItemID: value
        };

        searchFieldIsFocused.current = false;
        setState(newState);
        props.onChange(value);
    };

    let selectedItemsElement: React.JSX.Element;

    if (props.options.multiselect) {
        let selectedItems = '';

        _.each(props.checkedItems, (value, item) => {
            if (value) {
                selectedItems += selectedItems.length === 0 ? ' ' + item : ', ' + item;
            }
        });

        let selectedItemsStyle: React.CSSProperties;

        if (containerRef.current?.clientWidth > 0) {
            selectedItemsStyle = {
                maxWidth: containerRef.current?.clientWidth
            };
        }

        selectedItemsElement = (
            <div className="items-to-add" style={selectedItemsStyle}>
                <strong>{'Will be added:'}</strong> <span>{selectedItems}</span>
            </div>
        );
    }

    return (
        <div className="attribute-select listen-container-resize flex-grow" ref={containerRef}>
            <div className="d-flex flex-column" onKeyDown={handleOnKeyDown}>
                {selectedItemsElement}

                <div className="editor-element" tabIndex={0}>
                    <input
                        type="text"
                        className="editor-input"
                        ref={searchFieldRef}
                        value={state.editorValue}
                        placeholder={'Start typing here'}
                        spellCheck={false}
                        autoCorrect="off"
                        autoCapitalize="off"
                        autoComplete="off"
                        onInput={handleOnEditorChange}
                        onChange={() => {}}
                        onFocus={handleOnSearchFieldFocus}
                        onBlur={handleOnSearchFieldBlur}
                    />
                </div>

                <div className="content-body flex-grow" ref={treeContainerRef}>
                    <ul className="list-unstyled" ref={treeControlRef} tabIndex={1}>
                        <TreeItemGeneral
                            key={'root'}
                            shadow={true}
                            arr={state.tree}
                            select={state.value}
                            focusedItemID={state.focusedItemID}
                            collapsed={false}
                            collapsedDict={state.collapsedDict}
                            showCheckbox={props.options.multiselect === true}
                            checkedItems={props.checkedItems}
                            getCustomIcon={getCustomIcon}
                            onSelect={handleOnItemSelect}
                            onDoubleClick={handleOnDoubleClick}
                            onCollapsedChange={handleOnCollapsedTreeItemChange}
                            onLoadLeafCall={handleOnLoadLeafCall}
                            onCheckedChange={props.onCheckedChange}
                        />
                    </ul>
                </div>
            </div>
        </div>
    );
}

function getCustomIcon(obj: ITreeItem): React.JSX.Element {
    const result = [];

    if (obj.isKeyAttribute) {
        result.push(<span className="fa fa-key" key="key" title={'Key Attribute'} />);
    }

    if (obj.isDefaultAttribute) {
        result.push(<span className="fa fa-flag-o" key="default" title={'Default Attribute'} />);
    }

    switch (result.length) {
        case 0:
            return <span className="fa fa-folder" />;
        case 1:
            return result[0];
        default:
            return <div>{result}</div>;
    }
}
